import React, { useEffect, useState } from 'react';

import { Info, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
	useGetRevenueConfigurationsQuery,
	useGetTenantConfigQuery,
} from '../../api';
import {
	PARTNER_TYPE_LABELS,
	useCreatePartnerMutation,
	useGetPartnerQuery,
	useUpdatePartnerMutation,
} from '../../api/partners';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../providers/AppAuthProvider';
import { SkeletonCard } from '../common';
import Logo from '../Logo';

import PartnerLogo from './PartnerLogo';
import {
	btn,
	card,
	help,
	input,
	label,
	sectionTitle,
	select,
	selectChevron,
	SettingsPageHeader,
	Switch,
} from './settingsV9';

import type { PartnerFields, PartnerType } from '../../api/partners';

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];

const EMPTY_PARTNER: PartnerFields = {
	name: '',
	partner_type: 'bank',
	name_on_proposals: null,
	logo_data_url: null,
	show_logo: true,
	show_name: true,
	contact_name: null,
	contact_email: null,
	contact_phone: null,
	website: null,
	address: null,
	revenue_config_name: null,
	statement_mailbox: null,
};

type TextField = Exclude<
	keyof PartnerFields,
	'partner_type' | 'show_logo' | 'show_name' | 'logo_data_url'
>;

interface PartnerDetailProps {
	partnerId: string;
	onClose: () => void;
}

const PartnerDetail: React.FC<PartnerDetailProps> = ({
	partnerId,
	onClose,
}) => {
	const isNew = partnerId === 'new';
	const navigate = useNavigate();
	const { isAdmin } = useAuth();
	const { showToast } = useToast();
	const { data: partner, isLoading } = useGetPartnerQuery(partnerId, {
		skip: isNew,
	});
	const { data: revenueConfigs } = useGetRevenueConfigurationsQuery();
	const { data: tenantConfig } = useGetTenantConfigQuery();
	const [createPartner, { isLoading: isCreating }] = useCreatePartnerMutation();
	const [updatePartner, { isLoading: isUpdating }] = useUpdatePartnerMutation();
	const [form, setForm] = useState<PartnerFields>(EMPTY_PARTNER);

	useEffect(() => {
		if (partner) {
			const keys = Object.keys(EMPTY_PARTNER) as (keyof PartnerFields)[];
			setForm(
				Object.fromEntries(
					keys.map((key) => [key, partner[key]]),
				) as unknown as PartnerFields,
			);
		}
	}, [partner]);

	const set = <K extends keyof PartnerFields>(
		key: K,
		value: PartnerFields[K],
	) => setForm((current) => ({ ...current, [key]: value }));

	const field = (
		key: TextField,
		text: React.ReactNode,
		placeholder: string,
		extraClass = '',
	) => (
		<div>
			<label className={label}>{text}</label>
			<input
				className={`${input} ${extraClass}`}
				value={form[key] ?? ''}
				placeholder={placeholder}
				onChange={(e) => set(key, e.target.value || null)}
				disabled={!isAdmin}
			/>
		</div>
	);

	const handleLogo = (file: File | undefined) => {
		if (!file) return;
		if (!LOGO_TYPES.includes(file.type) || file.size > MAX_LOGO_BYTES) {
			showToast('Error', 'Use a PNG, JPEG or SVG up to 2 MB.', 'error');
			return;
		}
		const reader = new FileReader();
		reader.onload = () => set('logo_data_url', reader.result as string);
		reader.readAsDataURL(file);
	};

	const handleSave = async () => {
		if (!form.name.trim()) {
			showToast('Error', 'Partner name is required', 'error');
			return;
		}
		try {
			if (isNew) {
				await createPartner(form).unwrap();
			} else {
				await updatePartner({ id: partnerId, body: form }).unwrap();
			}
			showToast('Success', 'Partner saved', 'success');
			onClose();
		} catch (error) {
			const message = (error as { data?: { message?: string } })?.data?.message;
			showToast('Error', message || 'Failed to save partner', 'error');
		}
	};

	if (!isNew && isLoading) {
		return <SkeletonCard />;
	}

	const tenant = tenantConfig?.tenant;
	const scheduleNames = Object.keys(revenueConfigs?.data ?? {});
	const linkedSchedule = form.revenue_config_name
		? revenueConfigs?.data?.[form.revenue_config_name]?.[0]
		: undefined;
	const title = isNew ? 'Add partner' : partner?.name;
	const displayName = form.name_on_proposals || form.name || 'Partner name';
	const showsPartner = form.show_logo || form.show_name;
	const tenantLogo = (
		<Logo
			logoBase64={tenant?.logo_base64}
			alt={tenant?.name || 'RocketFee'}
			className="h-14 w-auto -my-1.5"
		/>
	);

	return (
		<div className="space-y-4">
			<div className="flex flex-col gap-2">
				<div className="flex items-center gap-1.5 text-[13px] text-gray-500">
					<button
						className="hover:text-gray-900"
						onClick={() => navigate('/settings?tab=enterprise')}
					>
						Settings
					</button>
					<span>›</span>
					<button className="hover:text-gray-900" onClick={onClose}>
						Partners
					</button>
					<span>›</span>
					<span>{isNew ? 'New partner' : partner?.name}</span>
				</div>
				<SettingsPageHeader
					title={title}
					subtitle={
						isNew
							? 'Set up the brand, the contact and the pricing program for a new partner.'
							: 'Brand, contact and pricing program for this partner.'
					}
					actions={
						<>
							<button className={btn.neutral} onClick={onClose}>
								Cancel
							</button>
							{isAdmin && (
								<button
									className={btn.primary}
									onClick={handleSave}
									disabled={isCreating || isUpdating}
								>
									{isNew ? 'Add partner' : 'Save changes'}
								</button>
							)}
						</>
					}
				/>
			</div>

			<div className="grid grid-cols-1 min-[1100px]:grid-cols-[minmax(0,1fr)_380px] gap-6 items-start">
				<div className="space-y-4">
					<div className={card}>
						<div className="p-6 space-y-4">
							<div className={sectionTitle}>Branding</div>
							<div>
								<label className={label}>Logo</label>
								<div className="flex items-center gap-5">
									<PartnerLogo partner={form} large />
									{isAdmin && (
										<div className="space-y-2">
											<div className="flex items-center gap-2">
												<label className={`${btn.secondarySm} cursor-pointer`}>
													<Upload className="h-4 w-4" />
													{form.logo_data_url ? 'Replace logo' : 'Upload logo'}
													<input
														type="file"
														accept={LOGO_TYPES.join(',')}
														hidden
														onChange={(e) => handleLogo(e.target.files?.[0])}
													/>
												</label>
												{form.logo_data_url && (
													<button
														className={btn.neutralSm}
														onClick={() => set('logo_data_url', null)}
													>
														Remove
													</button>
												)}
											</div>
											<div className={help}>
												PNG, JPEG or SVG, up to 2 MB. Without a logo, the
												initials show.
											</div>
										</div>
									)}
								</div>
							</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{field(
									'name',
									<>
										Partner name <span className="text-red-500">*</span>
									</>,
									'For example, Harbor Point Bank',
								)}
								{field(
									'name_on_proposals',
									'Name on proposals',
									'Same as the partner name',
								)}
							</div>
							<div className="flex flex-col gap-2.5">
								<div className="block text-sm font-medium text-gray-700">
									On proposals
								</div>
								<label className="flex items-center gap-2.5 text-sm text-gray-900 cursor-pointer">
									<Switch
										checked={form.show_logo}
										onChange={(value) => set('show_logo', value)}
										disabled={!isAdmin}
									/>
									<span>Show this partner's logo</span>
								</label>
								<label className="flex items-center gap-2.5 text-sm text-gray-900 cursor-pointer">
									<Switch
										checked={form.show_name}
										onChange={(value) => set('show_name', value)}
										disabled={!isAdmin}
									/>
									<span>Show this partner's name</span>
								</label>
							</div>
						</div>
					</div>

					<div className={card}>
						<div className="p-6 space-y-4">
							<div className={sectionTitle}>Contact details</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{field('contact_name', 'Contact name', 'Full name')}
								{field('contact_email', 'Email', 'name@partner.com')}
								{field('contact_phone', 'Phone', '(000) 555-0100')}
								{field('website', 'Website', 'partner.com')}
							</div>
							{field('address', 'Address', 'Street, city, state')}
						</div>
					</div>

					<div className={card}>
						<div className="p-6 space-y-4">
							<div className={sectionTitle}>Program</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<label className={label}>Partner type</label>
									<select
										className={select}
										style={selectChevron}
										value={form.partner_type}
										onChange={(e) =>
											set('partner_type', e.target.value as PartnerType)
										}
										disabled={!isAdmin}
									>
										{Object.entries(PARTNER_TYPE_LABELS).map(
											([value, text]) => (
												<option key={value} value={value}>
													{text}
												</option>
											),
										)}
									</select>
								</div>
								<div>
									<label className={label}>Revenue configuration</label>
									<select
										className={select}
										style={selectChevron}
										value={form.revenue_config_name ?? ''}
										onChange={(e) =>
											set('revenue_config_name', e.target.value || null)
										}
										disabled={!isAdmin}
									>
										<option value="">No Schedule A</option>
										{scheduleNames.map((name) => (
											<option key={name} value={name}>
												{name}
											</option>
										))}
									</select>
									{linkedSchedule && isAdmin && (
										<div className={help}>
											<button
												className={btn.link}
												onClick={() =>
													navigate(
														`/settings?tab=revenue&edit=${linkedSchedule.id}`,
													)
												}
											>
												Edit this Schedule A
											</button>
										</div>
									)}
								</div>
							</div>
							<div>
								{field(
									'statement_mailbox',
									'Statement mailbox',
									'partner@example.com',
									'font-mono text-xs',
								)}
								<div className={help}>
									Statements sent here use this partner's templates and Schedule
									A. Microsoft 365 watches this mailbox.
								</div>
							</div>
							<div className="flex items-center justify-between gap-3">
								<div>
									<div className="block text-sm font-medium text-gray-700">
										Pricing templates
									</div>
									<div className="text-xs text-gray-500">
										{partner?.template_count ?? 0} templates belong to this
										partner
									</div>
								</div>
								{!isNew && (
									<button
										className={btn.secondarySm}
										onClick={() => navigate('/settings?tab=pricing')}
									>
										View templates
									</button>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="space-y-4">
					<div>
						<div className={label}>Proposal header preview</div>
						<div className="flex items-center gap-3.5 px-5 py-[18px] border border-gray-200 rounded-lg bg-white">
							{showsPartner ? (
								<>
									{form.show_logo &&
										(form.logo_data_url ? (
											<img
												src={form.logo_data_url}
												alt=""
												className="h-11 w-11 object-contain rounded-lg"
											/>
										) : (
											<PartnerLogo partner={form} />
										))}
									{form.show_name && (
										<div>
											<div className="font-semibold text-primary-500 text-[15px] leading-tight">
												{displayName}
											</div>
											<div className="text-xs text-gray-500">
												Merchant services
											</div>
										</div>
									)}
									<span className="w-px self-stretch bg-gray-200" />
									{tenantLogo}
								</>
							) : (
								<>
									{tenantLogo}
									<span className="ml-auto text-xs text-gray-500">
										{tenant?.name || 'Your'} brand only
									</span>
								</>
							)}
						</div>
					</div>
					<div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
						<Info className="h-5 w-5 flex-none mt-px text-blue-600" />
						<div>
							Merchants see the partner first. {tenant?.name || 'Your company'}{' '}
							stays on every proposal as the processor.
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PartnerDetail;
